import { useState, useEffect, useCallback, useRef } from "react";
import { apiGet, apiSend, apiFetch, apiDownload, errorMessage } from "./api";
import { GestionHeader } from "./SiteHeader";
import CreateOrgModal from "./CreateOrgModal";
import "./QrLinks.css";

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Prénom + initiale du nom : « Amir Bendjelid » → « Amir B ». Le champ
// `surname` porte le prénom et `name` le nom de famille (cf. l'affichage du
// header, « {surname} {name} »).
function shortName(user) {
  return `${user.surname} ${(user.name || "").charAt(0).toUpperCase()}`;
}

// État d'un QR : libre tant qu'aucun commerce ne lui est rattaché, actif une
// fois attribué, inactif si ce commerce est en défaut de paiement.
function qrState(link) {
  if (!link.organization_id) return { key: "stock", label: "En stock" };
  if (link.payment_default) return { key: "inactive", label: "Inactif" };
  return { key: "active", label: "Actif" };
}

function QrLinkRow({ link, orgs, onAssign, onLabel, onDelete, selected, onToggle, assignees, onOwner }) {
  const [label, setLabel] = useState(link.label || "");
  const [copied, setCopied] = useState(false);

  const copyTimer = useRef(null);
  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback : sélection manuelle possible sur le <code>
    }
  };

  // L'étiquette courante est lue au moment de l'envoi (et non depuis les props)
  // pour qu'un changement d'organisation ne réécrase pas une saisie en cours.
  const saveLabel = () => {
    if (label.trim() !== (link.label || "")) {
      onLabel(link, label.trim());
    }
  };

  const assign = (orgId) => {
    saveLabel();
    onAssign(link, orgId, label.trim());
  };

  const state = qrState(link);

  return (
    <tr className={selected ? "ql-row-selected" : ""}>
      <td className="ql-col-check">
        <input
          type="checkbox"
          className="ql-check"
          checked={selected}
          onChange={() => onToggle(link.id)}
          aria-label={`Sélectionner ${link.id} pour l'impression`}
        />
      </td>
      <td className="ql-col-id">
        <div className="ql-id-block">
          <span className="ql-id">{link.id}</span>
          <span className="ql-date">{formatDate(link.created_at)}</span>
          <button className="ql-btn-copy" onClick={copyUrl}>
            {copied ? "Copié !" : "Copier l'URL"}
          </button>
        </div>
      </td>
      <td className="ql-col-org">
        <select
          value={link.organization_id || ""}
          onChange={(e) => assign(e.target.value ? parseInt(e.target.value) : null)}
          aria-label="Commerce de redirection"
        >
          <option value="">Libre</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </td>
      <td className="ql-col-sale">
        {assignees ? (
          <select
            // Couleur d'identification du commercial : portée par le select
            // lui-même, la valeur sélectionnée étant rendue par le système et
            // non par l'<option> correspondante.
            style={link.created_by_color ? { color: link.created_by_color } : undefined}
            value={link.created_by || ""}
            onChange={(e) => onOwner(link, e.target.value ? parseInt(e.target.value) : null)}
            aria-label="Commercial attributaire"
            title="Commercial (ou admin) à qui ce QR est attribué"
          >
            {!link.created_by && <option value="">Non attribué</option>}
            {link.created_by && !assignees.some((u) => u.id === link.created_by) && (
              <option value={link.created_by}>
                {link.created_by_short || `utilisateur #${link.created_by}`}
              </option>
            )}
            {assignees.map((u) => (
              <option key={u.id} value={u.id} style={u.color ? { color: u.color } : undefined}>
                {shortName(u)}{u.role === "Admin" ? " (admin)" : ""}
              </option>
            ))}
          </select>
        ) : (
          <span
            className="ql-sale-name"
            style={link.created_by_color ? { color: link.created_by_color } : undefined}
          >
            {link.created_by_short || "—"}
          </span>
        )}
      </td>
      <td className="ql-col-state">
        <span className={`ql-badge ql-badge-${state.key}`}>{state.label}</span>
      </td>
      <td className="ql-col-label">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={saveLabel}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          placeholder="Étiquette (ex : salon 2026...)"
          maxLength={100}
          aria-label="Étiquette du QR"
        />
      </td>
      <td className="ql-col-actions">
        <button className="ql-btn-delete" onClick={() => onDelete(link)}>Supprimer</button>
      </td>
    </tr>
  );
}

function QrLinks({ user, onNavigate, onLogout }) {
  const [links, setLinks] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [printing, setPrinting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // null tant que la liste n'est pas chargée (ou pour un non-admin)
  const [assignees, setAssignees] = useState(null);
  // Compteurs globaux réservés à l'admin : null tant qu'ils ne sont pas chargés
  const [stats, setStats] = useState(null);
  // Un commercial signe un nouveau commerce : il crée l'organisation ici,
  // puis lui assigne un QR — sans passer par un admin.
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  // "" (tous) | "active" | "stock" | "inactive" — mêmes clés que qrState()
  const [stateFilter, setStateFilter] = useState("");

  const fetchLinks = useCallback(async () => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (search.trim()) params.append("search", search.trim());
    if (ownerFilter) params.append("created_by", ownerFilter);
    if (stateFilter) params.append("state", stateFilter);
    try {
      const data = await apiGet(`/api/qr-links?${params}`,
        "Impossible de charger les QR dynamiques");
      setLinks(data.qr_links);
      setTotalPages(data.pages || 1);
      setError("");
    } catch (e) {
      setLinks([]);
      setError(errorMessage(e, "Impossible de charger les QR dynamiques"));
    }
    setLoading(false);
  }, [page, search, ownerFilter, stateFilter]);

  // Debounce : sans lui, une requête part à chaque frappe dans la recherche
  useEffect(() => {
    const timer = setTimeout(fetchLinks, 300);
    return () => clearTimeout(timer);
  }, [fetchLinks]);

  const fetchOrgs = useCallback(() => {
    apiGet("/api/organizations").then(setOrgs).catch(() => setOrgs([]));
  }, []);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // Compteurs globaux : portent sur toute la base, indépendamment des filtres
  // et de la pagination. Silencieux en cas d'échec — la page reste utilisable
  // sans eux, ce n'est pas la raison d'être de l'écran.
  const fetchStats = useCallback(() => {
    if (user.role !== "Admin") return;
    apiGet("/api/qr-links/stats").then(setStats).catch(() => setStats(null));
  }, [user.role]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (user.role !== "Admin") return;
    // Attributaires possibles : les commerciaux d'abord, puis les admins
    Promise.all([
      apiGet("/api/users?role=Sale&limit=100"),
      apiGet("/api/users?role=Admin&limit=100"),
    ])
      .then(([sales, admins]) => setAssignees([...sales.users, ...admins.users]))
      .catch(() => setAssignees(null));
  }, [user.role]);

  const handleCreate = async () => {
    setCreating(true);
    setError("");
    try {
      await apiSend("/api/qr-links", "POST", {}, "Impossible de créer le QR");
      // Le nouveau QR est le plus récent : il apparaît en tête de la page 1
      if (page !== 1) setPage(1);
      else await fetchLinks();
      fetchStats();
    } catch (e) {
      setError(errorMessage(e, "Impossible de créer le QR"));
    }
    setCreating(false);
  };

  const updateLink = async (link, body, apply) => {
    setError("");
    try {
      await apiSend(`/api/qr-links/${link.id}`, "PUT", body, "Erreur lors de la mise à jour");
      setLinks((prev) => prev.map((l) => (l.id === link.id ? apply(l) : l)));
      // Assigner (ou retirer) un commerce déplace le QR entre les compteurs
      // « attribués » et « non attribués »
      fetchStats();
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de la mise à jour"));
    }
  };

  const handleAssign = (link, orgId, currentLabel) => {
    const org = orgId ? (orgs.find((o) => o.id === orgId) || {}) : {};
    // `currentLabel` vient du champ affiché : envoyer link.label écraserait une
    // étiquette tout juste saisie mais pas encore reflétée dans les props.
    const label = currentLabel !== undefined ? currentLabel : (link.label || "");
    // payment_default suit le commerce : sans lui, la colonne État resterait
    // sur « Actif » après rattachement à un commerce en défaut de paiement.
    updateLink(link, { organization_id: orgId, label: label || null },
      (l) => ({
        ...l,
        organization_id: orgId,
        organization: org.name || null,
        payment_default: org.payment_default || 0,
        label,
      }));
  };

  const handleLabel = (link, label) => {
    updateLink(link, { organization_id: link.organization_id, label },
      (l) => ({ ...l, label }));
  };

  const handleOwner = async (link, userId) => {
    if (!userId || userId === link.created_by) return;
    setError("");
    try {
      const res = await apiSend(`/api/qr-links/${link.id}/owner`, "PUT",
        { user_id: userId }, "Erreur lors de l'attribution");
      setLinks((prev) => prev.map((l) => (l.id === link.id
        ? {
            ...l,
            created_by: res.created_by,
            created_by_name: res.created_by_name,
            created_by_short: res.created_by_short,
            created_by_color: res.created_by_color,
          }
        : l)));
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de l'attribution"));
    }
  };

  const handleDelete = async (link) => {
    const name = link.label ? `${link.id} (${link.label})` : link.id;
    if (!window.confirm(
      `Supprimer le QR ${name} ?\n\nCette action est définitive : un autocollant déjà imprimé avec ce QR affichera « QR code inconnu ».`
    )) return;
    setError("");
    try {
      await apiFetch(`/api/qr-links/${link.id}`, { method: "DELETE" }, "Erreur lors de la suppression");
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(link.id);
        return next;
      });
      // Recharge pour recompléter la page ; recule si elle devenait vide
      if (links.length === 1 && page > 1) setPage(page - 1);
      else await fetchLinks();
      fetchStats();
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de la suppression"));
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const plural = ids.length > 1;
    if (!window.confirm(
      `Supprimer ${plural ? `les ${ids.length} QR sélectionnés` : "le QR sélectionné"} ?\n\n`
      + `Cette action est définitive : un autocollant déjà imprimé avec ${plural ? "un de ces QR" : "ce QR"} affichera « QR code inconnu ».`
    )) return;
    setDeleting(true);
    setError("");
    // Suppressions séquentielles : la sélection peut couvrir plusieurs pages
    const failed = [];
    for (const id of ids) {
      try {
        await apiFetch(`/api/qr-links/${id}`, { method: "DELETE" }, "Erreur lors de la suppression");
      } catch {
        failed.push(id);
      }
    }
    // Les échecs restent sélectionnés, pour réessayer d'un clic
    setSelected(new Set(failed));
    if (failed.length > 0) {
      setError(`Impossible de supprimer : ${failed.join(", ")}`);
    }
    if (page !== 1) setPage(1);
    else await fetchLinks();
    fetchStats();
    setDeleting(false);
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Coche/décoche la page affichée sans toucher aux sélections des autres pages
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = links.every((l) => next.has(l.id));
      links.forEach((l) => (allSelected ? next.delete(l.id) : next.add(l.id)));
      return next;
    });
  };

  const handlePrint = async () => {
    // La sélection peut couvrir plusieurs pages : ordre = ordre de sélection
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setPrinting(true);
    setError("");
    try {
      await apiDownload(
        "/api/qr-links/print",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        },
        "flyers-qr.pdf",
        "Impossible de générer les flyers"
      );
      // Le téléchargement marque les QR comme imprimés côté serveur
      fetchStats();
    } catch (e) {
      setError(errorMessage(e, "Impossible de générer les flyers"));
    }
    setPrinting(false);
  };

  return (
    <div className="ql-container">
      {/* Retour : dashboard admin pour un admin, menu principal pour un commercial */}
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="QR Dynamiques"
        onBack={() => onNavigate(user.role === "Sale" ? "home" : "admin")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      >
      </GestionHeader>

      <main className="ql-main">
        <div className="ql-toolbar">
          <p className="ql-intro">
            Chaque QR imprimé garde la même URL : assignez-lui un commerce pour
            activer la redirection vers la création de carte (Android/iOS détecté
            automatiquement), ou laissez-le sur le placeholder.
          </p>
          <div className="ql-toolbar-actions">
            <button className="rc-btn rc-btn-outline" onClick={() => setShowCreateOrg(true)}>
              + Nouveau commerce
            </button>
            <button className="rc-btn rc-btn-primary" onClick={handleCreate} disabled={creating}>
              {creating ? "Création..." : "+ Créer un QR"}
            </button>
          </div>
        </div>

        {error && <div className="ql-error">{error}</div>}

        {/* Compteurs globaux (admin) : portent sur l'ensemble des QR, sans
            tenir compte des filtres ni de la pagination ci-dessous. */}
        {user.role === "Admin" && stats && (
          <div className="ql-stats">
            <div className="ql-stat">
              <span className="ql-stat-value">{stats.created}</span>
              <span className="ql-stat-label">QR créés</span>
            </div>
            <div className="ql-stat">
              <span className="ql-stat-value">{stats.printed}</span>
              <span className="ql-stat-label">Imprimés</span>
            </div>
            <div className="ql-stat">
              <span className="ql-stat-value">{stats.assigned}</span>
              <span className="ql-stat-label">Attribués à un commerce</span>
            </div>
            <div className="ql-stat">
              <span className="ql-stat-value">{stats.unassigned}</span>
              <span className="ql-stat-label">Non attribués</span>
            </div>
          </div>
        )}

        <div className="ql-filters">
          <input
            type="text"
            className="ql-filter-search"
            placeholder="Rechercher par ID (ex : QR-1)..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          {user.role === "Admin" && assignees && (
            <select
              value={ownerFilter}
              onChange={(e) => { setOwnerFilter(e.target.value); setPage(1); }}
              aria-label="Filtrer par commercial"
            >
              <option value="">Tous les commerciaux</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.surname} {u.name}{u.role === "Admin" ? " (admin)" : ""}
                </option>
              ))}
            </select>
          )}
          <select
            value={stateFilter}
            onChange={(e) => { setStateFilter(e.target.value); setPage(1); }}
            aria-label="Filtrer par état"
          >
            <option value="">Tous les états</option>
            <option value="active">Actif</option>
            <option value="stock">En stock</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>

        {links.length > 0 && (
          <div className="ql-printbar">
            <label className="ql-printbar-all">
              <input
                type="checkbox"
                checked={links.length > 0 && links.every((l) => selected.has(l.id))}
                onChange={toggleAll}
              />
              Sélectionner la page
            </label>
            {selected.size > 0 && (
              <button className="ql-btn-clear" onClick={() => setSelected(new Set())}>
                Tout désélectionner
              </button>
            )}
            <button
              className="ql-btn-delete"
              onClick={handleDeleteSelected}
              disabled={deleting || selected.size === 0}
            >
              {deleting
                ? "Suppression..."
                : `Supprimer les QR sélectionnés (${selected.size})`}
            </button>
            <button
              className="rc-btn rc-btn-primary"
              onClick={handlePrint}
              disabled={printing || selected.size === 0}
            >
              {printing
                ? "Génération du PDF..."
                : `Imprimer les flyers (${selected.size})`}
            </button>
          </div>
        )}

        {loading ? (
          <p className="ql-empty">Chargement...</p>
        ) : links.length === 0 ? (
          <p className="ql-empty">
            {search.trim() || ownerFilter || stateFilter
              ? "Aucun QR ne correspond à ces filtres."
              : "Aucun QR dynamique pour le moment — créez le premier."}
          </p>
        ) : (
          <div className="ql-table-scroll">
            <table className="ql-table">
              <thead>
                <tr>
                  <th scope="col" className="ql-col-check">
                    <span className="ql-sr-only">Sélection</span>
                  </th>
                  <th scope="col" className="ql-col-id">QR code</th>
                  <th scope="col" className="ql-col-org">Commerce</th>
                  <th scope="col" className="ql-col-sale">Commercial</th>
                  <th scope="col" className="ql-col-state">État</th>
                  <th scope="col" className="ql-col-label">Étiquette</th>
                  <th scope="col" className="ql-col-actions">
                    <span className="ql-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <QrLinkRow
                    key={link.id}
                    link={link}
                    orgs={orgs}
                    onAssign={handleAssign}
                    onLabel={handleLabel}
                    onDelete={handleDelete}
                    selected={selected.has(link.id)}
                    onToggle={toggleSelected}
                    assignees={assignees}
                    onOwner={handleOwner}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="ql-pagination">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Précédent</button>
            <span>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Suivant</button>
          </div>
        )}
      </main>

      {showCreateOrg && (
        <CreateOrgModal
          onClose={() => setShowCreateOrg(false)}
          onCreated={() => {
            setShowCreateOrg(false);
            // Le nouveau commerce apparaît aussitôt dans les sélecteurs des QR
            fetchOrgs();
          }}
        />
      )}
    </div>
  );
}

export default QrLinks;
